<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import BrandLogo from '@/components/common/BrandLogo.vue'
import GroupSelector from '@/components/header/GroupSelector.vue'
import HeaderClock from '@/components/header/HeaderClock.vue'
import LanguageSwitcher from '@/components/header/LanguageSwitcher.vue'
import ServerSelector from '@/components/header/ServerSelector.vue'
import UserMenu from '@/components/header/UserMenu.vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'

const props = withDefaults(defineProps<{ withSidebarToggle?: boolean }>(), {
  withSidebarToggle: false,
})

const { t } = useI18n()
const auth = useAuthStore()
const ui = useUiStore()
</script>

<template>
  <header class="chrome-surface border-gold-600/60 sticky top-0 z-40 border-b-2 shadow-raised">
    <div class="flex h-12 items-center gap-2 px-3 sm:gap-3 sm:px-4 md:h-14">
      <!--
        Same hamburger at every width, two different jobs: below lg the sidebar
        is an off-canvas drawer and this opens it, from lg up it is docked and
        this narrows it to its icon rail. Two buttons swapped by a media query
        rather than one reading the viewport in JS, so the correct control is
        already in the DOM at first paint.
      -->
      <template v-if="props.withSidebarToggle">
        <button
          type="button"
          class="chrome-item cursor-pointer px-2 lg:hidden"
          :aria-label="t('header.openMenu')"
          @click="ui.toggleMobileSidebar()"
        >
          <AppIcon name="mdi-menu" class="text-xl" />
        </button>
        <button
          type="button"
          class="chrome-item hidden cursor-pointer px-2 lg:inline-flex"
          :aria-label="ui.sidebarCollapsed ? t('sidebar.expand') : t('sidebar.collapse')"
          :title="ui.sidebarCollapsed ? t('sidebar.expand') : t('sidebar.collapse')"
          :aria-expanded="!ui.sidebarCollapsed"
          @click="ui.toggleSidebar()"
        >
          <AppIcon name="mdi-menu" class="text-xl" />
        </button>
      </template>

      <RouterLink :to="{ name: 'home' }" class="shrink-0">
        <BrandLogo />
      </RouterLink>

      <!--
        Language sits right next to the brand, for guests and users alike; the
        wrapper's `mr-auto` is what pushes everything after it to the right.
        Login and register don't use this header: AuthLayout keeps its own
        switcher where it was.
      -->
      <div class="mr-auto flex items-center">
        <LanguageSwitcher align="start" />
      </div>

      <!--
        Guest: clock + the two calls to action. Below `sm` the two
        CTAs move to their own row — same trick as the server selector further
        down. Not only when the sidebar toggle is in the row: the Cinzel
        wordmark alone pushed the landing header to ~400px, past a 349-390px
        phone, so the narrow layout is two rows for every guest.
      -->
      <template v-if="!auth.isAuthenticated">
        <HeaderClock />
        <RouterLink
          :to="{ name: 'login' }"
          class="btn-ghost !text-parchment-100 hidden hover:!bg-white/10 sm:inline-flex"
        >
          {{ t('common.logIn') }}
        </RouterLink>
        <RouterLink :to="{ name: 'register' }" class="btn-gold hidden sm:inline-flex">
          {{ t('common.signUp') }}
        </RouterLink>
      </template>

      <!-- Authenticated: context selectors, clock, user menu. -->
      <template v-else>
        <div class="hidden min-w-0 items-center gap-2 md:flex">
          <GroupSelector />
          <ServerSelector />
        </div>
        <HeaderClock />
        <UserMenu />
      </template>
    </div>

    <!--
      Below md the first row has no space left, so the server selector gets its
      own row right under it — it is the piece of context worth keeping visible
      at all times. The group selector moves to the top of the sidebar drawer
      instead (see AppSidebar), which keeps this row to a single line.
      Both rows are a notch shorter than from md up (h-12 here, py-1 and the
      `header-compact` controls below): on a phone every pixel of header is
      one less for the page.
    -->
    <div
      v-if="auth.isAuthenticated"
      class="header-compact flex items-center gap-2 border-t border-white/10 px-3 py-1 md:hidden"
    >
      <ServerSelector />
    </div>

    <div
      v-if="!auth.isAuthenticated"
      class="flex items-center justify-center gap-2 border-t border-white/10 px-3 py-2 sm:hidden"
    >
      <RouterLink :to="{ name: 'login' }" class="btn-ghost !text-parchment-100 hover:!bg-white/10">
        {{ t('common.logIn') }}
      </RouterLink>
      <RouterLink :to="{ name: 'register' }" class="btn-gold">
        {{ t('common.signUp') }}
      </RouterLink>
    </div>
  </header>
</template>

<style scoped>
/* Second mobile row: the server controls lose a little vertical padding (32px instead of 36px). */
.header-compact :deep(.chrome-item) {
  padding-block: 0.375rem;
}
</style>
